import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  Input,
  AfterViewInit,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { COUNTRY_CODE, CountryConfigService } from '@cemex-core/cmx-api';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, startWith, switchMap, take, tap } from 'rxjs/operators';

import { HEADER_MENU_ITEMS } from '../models/header-menu-items.enum';
import { MOBILE_LINKS } from '../models/mobile-links.enum';
import { MenuService } from '../services/menu.service';
import { AuthService } from '../services/auth.service';
import {
  getLanguages,
  getLanguage,
  getAuth,
  getLegalEntity,
  getLoader,
  isInitialized,
  getDirection,
  getAppState,
  getVersion,
  getCopyright,
} from '../store/selectors';
import { ConfigService } from './../services/config.service';
import { SetLegalEntity, Initialize, SetMobile, SetDirection, SetVersion } from '../store/actions';
import { ILegalEntity } from '@cemex-core/cmx-api/models/legal-entities/legal-entity';
import { Language } from '../typings/language';
import { LegalEntitiesService } from '../services/legal-entities.service';
import { FOOTER_LINKS } from '../models/footer-lints.enum';
import { RTL_COUNTRIES, RTL_LANGUAGES } from '../models/rtl';
import { APP_ALERT_OUTPUT } from '../models/alert.const';
import { State } from '../typings/state';
import { AuthState } from '../typings';
import { Router } from '@angular/router';
import {
  IQSI,
  IQualtricDialog,
  IQualtricDialogBody,
  qualtricDialogConfig,
  qualtricDialogBodyConfig,
} from '../models/qualtrics-dialog-config';
import { WindowRef } from '../services/windowRef.service';
import { CwcModal } from '@cmx-web-components/angular';
import { USER_TYPE } from '../models/user-type.enum';
import { ParametersService } from '../services/parameters.service';
import { CopyrightService } from '../services/copyright.service';
import { LanguageService } from '../services/language.service';
import { SessionService, Broadcaster, CountlyService, CmxAuthProfile } from '@cemex-core/angular-services-v8';
import { ILanguage } from '../cmx-layout/types/language';
import { Direction } from '../cmx-layout/types/directions';
import { ApplicationMenu } from '../cmx-layout/types/menu';
import { IFooterLink } from '../cmx-layout/types/footerLink';
import { IMobileLink } from '../cmx-layout/types/mobileLink';
import { IHeaderMenu } from '../cmx-layout/types/headerMenu';
import { IAppMenuItem } from '../cmx-layout/types/appMenu';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features/models/features.enum';
import { LoadingOverlayService } from '../../services/loading-overlay.service';
import { IamAuthService } from '@cemex-core/msal-services-v1';
import { validateAndSanitizeUrl, safeNavigate, safeWindowOpen } from '../../shared/utils/url-security.util';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'cmx-client-layout',
  templateUrl: './cmx-client-layout.component.html',
  styleUrls: ['./cmx-client-layout.component.scss'],
})
export class CmxClientLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private initCalled = false;
  private subs: Subscription[] = [];

  internalLoader = true;
  loader = true;
  chatbot = false; // TODO from config
  languageSelector = true; // TODO from config
  initialized = false;

  languages: Observable<ILanguage[]>;
  languagesRaw: Language[] = [];
  languageActive: Observable<string>;
  languageActiveISO: string;
  languageChange = new EventEmitter<string>();
  userType$ = new BehaviorSubject<string>(null);
  userTypeEnum = USER_TYPE;

  direction: Direction = 'ltr';

  mainAlertOutput = APP_ALERT_OUTPUT;

  isFeedbackEnabled = false;
  feedbackUrl: SafeResourceUrl;
  // @ViewChild('feedbackDialog')
  // private feedbackDialog: CmxDialogComponent

  authState: AuthState;
  user: CmxAuthProfile;
  userEmail: string;
  userInitials: string;
  userName: string;

  neorisLogo = false;
  entityModalOpen = false;
  logo = 'cemex_mono_white.svg';

  menuItems: Observable<ApplicationMenu[]>;
  headerMenuItems: Observable<IHeaderMenu[]>;
  footerItems: Observable<IFooterLink[]>;
  mobileMenuItems: Observable<IMobileLink[]>;
  supportContact: IHeaderMenu = {
    label: this.translate.instant('views.header.modal.contactTitle'),
    id: HEADER_MENU_ITEMS.CONTACT,
  };

  legalEntityList: Observable<ILegalEntity[]>;
  legalEntityMessage: Observable<string>;
  legalEntityLooader: Observable<boolean>;
  legalEntityActive: Observable<ILegalEntity>;

  country: COUNTRY_CODE;
  contactEmails: string[] = [];
  contactPhones: string[] = [];
  @ViewChild('countrySupportContactModal', { static: true })
  private countrySupportContactModal: CwcModal;

  isGreen = false;

  // DCM020CX1117-232
  // No translations for this part of application.
  appMenu = true;
  appMenuLabel = 'CEMEX Apps';
  appMenuItems: IAppMenuItem[];

  selectedlegalEntity: ILegalEntity;
  selectedLanguage: Language;

  private BLUE_GREEN_COOKIE_NAME = 'testingmode';
  private GREEN_COOKIE_VALUE = 'all';

  @Input() public qualtricsId = 'Q_ZID=ZN_9Gh8IPzaMlwxgmq';
  public dialogQualtricsConf: IQualtricDialog = qualtricDialogConfig;
  public dialogBodyConfig: IQualtricDialogBody = qualtricDialogBodyConfig;

  // new version
  version = 3;
  ServiceCenterApp = 'CEMEX Apps';
  userCountry = '';
  isLayoutRendered = false;
  isEntitySelectorRendered = false;
  showChatbot = false;
  chatbotRealocated = false;

  labelCopyright: Observable<string>;
  public isGetCopyright =
    window['CX_SHOW_COPYRIGHT'] && (window['CX_SHOW_COPYRIGHT'] === 'true' || window['CX_SHOW_COPYRIGHT'] === true)
      ? true
      : false;

  constructor(
    private store: Store<State>,
    private menuService: MenuService,
    public translate: TranslateService,
    private legalEntityService: LegalEntitiesService,
    private sanitizer: DomSanitizer,
    private auth: AuthService,
    private countryConfig: CountryConfigService,
    private cdRef: ChangeDetectorRef,
    private configService: ConfigService,
    public router: Router,
    private countlyService: CountlyService,
    private broadcaster: Broadcaster,
    private winRef: WindowRef,
    private parametersService: ParametersService,
    private copyrightService: CopyrightService,
    private languageService: LanguageService,
    private sessionService: SessionService,
    private featureToggle: FeatureToggleService,
    public loadingOverlayService: LoadingOverlayService,
    private iamService: IamAuthService,
  ) {
    this.languageSelector = !this.configService.getConfig().production;
    this.broadcaster.on<string>('Aivo_Chatbot').subscribe((response) => {
      if (JSON.parse(response) === 'true') {
        this.chatbot = true;
      } else {
        this.chatbot = false;
      }
    });
  }

  ngOnInit() {
    this.setLanguages();
    this.setMobileLinks();
    this.setHeaderMenu();
    this.setFooterLinks();
    this.setBlueGreen();
    this.setLegalEntity();
    this.setAppMenu();
    this.setVersion();
    this.subs.push(
      this.featureToggle.onConfigurationFetched
        .pipe(
          startWith({}),
          switchMap(() => this.store.select(getLanguage)),
        )
        .subscribe(() => {
          this.showChatbot = this.featureToggle.isFeatureEnabled(Features.ChatBotAivo);
          this.chatbotRealocated = this.featureToggle.isFeatureEnabled(Features.ChatBotAivoRealocated);
        }),
      this.store.select(getAuth).subscribe((auth) => {
        if (auth && !this.initCalled) {
          this.authState = auth;
          this.user = auth.profile;
          this.country = auth.country as COUNTRY_CODE;
          this.init();
          this.initCalled = true;
        }
      }),
      this.store.select(getLanguage).subscribe((langISO) => {
        this.languageActiveISO = langISO;
        this.detectDirection(langISO);
        this.getFeedbackLinks(langISO);
        this.userCountry = this.sessionService.country;
      }),
      this.store.select(getLoader).subscribe((loader) => (this.loader = loader)),
      this.store.select(isInitialized).subscribe((initialized) => (this.initialized = initialized)),
      this.store.select(getDirection).subscribe((value) => (this.direction = value)),
      this.store.select(getAppState).subscribe((state) => {
        this.selectedLanguage = state.languages.find((language) => language.languageISO === state.language);
        if (state.legalEntity) {
          this.selectedlegalEntity = state.legalEntity;
        }
      }),
      this.store.select(getVersion).subscribe((version) => {
        this.version = version;
      }),
      this.store
        .select(getLegalEntity)
        .pipe(
          filter((entity) => !!entity),
          map((entity) => entity.legalEntityTypeCode),
          distinctUntilChanged(),
          skip(1),
          tap((legalEntityTypeCode) => this.countlyService.addGTMCustomerChange(legalEntityTypeCode)),
        )
        .subscribe(),
    );
    this.userType$ = this.legalEntityService.userType$;
  }

  setVersion() {
    this.version = window['CX_VERSION'] === '2' ? 2 : 3;
  }

  ngAfterViewInit() {
    this.loadQualtrics();
  }

  // Dependend on the user. Must be called after authentication
  private init() {
    this.setUser();
    this.setContactUsInfo();
    this.setLogo();
    this.fetchItems();
    this.setFeedback();
    this.loadParameters();
    this.loadCopyright();
  }

  onLogout() {
    if (this.iamService.isLoginByIAM()) {
      this.iamService.logoutIAM();
    } else {
      this.auth.logout();
    }
  }

  onLogoClick() {
    safeNavigate('/');
  }

  onMenuClick(id: number) {
    const endpoint = this.menuService.getMenuItemEndpoint(id);

    // Validate the endpoint first
    const validatedEndpoint = validateAndSanitizeUrl(endpoint);
    if (!validatedEndpoint) {
      console.error('Blocked potentially unsafe menu endpoint:', endpoint);
      return;
    }

    if (
      validatedEndpoint.includes('/ordersnproduct/app/project-profiles') ||
      validatedEndpoint.includes('/ordersnproduct/app/orders-products-catalog/orders') ||
      validatedEndpoint.includes('/ordersnproduct/app/orders') ||
      validatedEndpoint.includes('/ordersnproduct/app/orders?one-click=0001') ||
      validatedEndpoint.includes('/ordersnproduct/app/orders?one-click=0002') ||
      validatedEndpoint.includes('/ordersnproduct/app/one-click/profiles')
    ) {
      const newEndpoint = validatedEndpoint.substring(validatedEndpoint.toString().indexOf('/', 1));
      this.router.navigateByUrl(newEndpoint);
    } else {
      safeNavigate(validatedEndpoint);
    }
  }

  onUserClick() {
    safeNavigate('/profile');
  }

  onFooterLabelClick() {
    safeNavigate('/copyright');
  }

  onLanguageChange(languageISO: string) {
    this.languageService.updateLanguage(languageISO);
  }

  onHeaderMenuClick(id: number) {
    switch (id) {
      case HEADER_MENU_ITEMS.SETTINGS:
        this.onSettingsClick();
        break;
      case HEADER_MENU_ITEMS.CONTACT:
        this.countrySupportContactModal.open = true;
        break;
    }
  }

  onFooterLinkClick(id: number) {
    switch (id) {
      case FOOTER_LINKS.LEGAL:
        this.onLegalClick();
        break;
      case FOOTER_LINKS.PRIVACY:
        this.onPrivacyClick();
        break;
      case FOOTER_LINKS.CMX_WEBSITE:
        this.onCemexClick();
        break;
    }
  }

  onMobileLinkClick(id: number) {
    switch (id) {
      case MOBILE_LINKS.LEGAL:
        this.onLegalClick();
        break;
      case MOBILE_LINKS.SETTINGS:
        this.onSettingsClick();
        break;
      case MOBILE_LINKS.PRIVACY:
        this.onPrivacyClick();
        break;
    }
  }

  onFeedbackClick() {
    // Qualtrics modal is opened with the onClick event of the feedback div
    if (this.version === 3) {
      const feedbackButton = document.getElementById('cmx-header-feedback');
      if (feedbackButton) {
        feedbackButton.click();
      }
    }
  }

  onLegalEntityChange(entity: number) {
    this.isEntitySelectorRendered = true;
    this.legalEntityService.setActiveLegalEntity(entity);
  }

  onLegalEntitySearch(key: string) {
    this.legalEntityService.searchLegalEntity(key);
  }

  onLegalEntityEnd() {
    this.legalEntityService.loadNextPage();
  }

  onChatboxClick() {}

  onMobileChange(value: boolean) {
    this.store.dispatch(new SetMobile(value));
  }

  onAppMenuClick(id: string) {
    const clickedItem = this.appMenuItems.find((item) => item.id === id);
    if (clickedItem && clickedItem.link) {
      const validatedUrl = validateAndSanitizeUrl(clickedItem.link);
      if (validatedUrl) {
        window.location.replace(validatedUrl);
      } else {
        console.error('Blocked potentially unsafe app menu link:', clickedItem.link);
      }
    } else {
      console.error('App menu item not found or missing link:', id);
    }
  }

  onVersionClick() {
    this.store.dispatch(new SetVersion(this.version === 2 ? 3 : 2));
  }

  loadQualtrics(): void {
    function loadScript(url: string, callback: () => void): void {
      const script: any = document.createElement('script');
      script.type = 'text/javascript';
      script.onload = () => {
        callback();
      };
      script.src = url;
      document.getElementsByTagName('head')[0].appendChild(script);
    }

    loadScript('https://zn9gh8ipzamlwxgmq-cemexglobal.siteintercept.qualtrics.com/SIE/?' + this.qualtricsId, () => {
      // the qualtrics api is instantiated and run
      const QAPI: IQSI = this.winRef.nativeWindow.QSI.API;
      if (QAPI) {
        QAPI.run();
      }
    });
    // this subscription is for you to reset the form when you close the dialog.
    // this.feedbackDialog.afterClosed().subscribe(() => {
    //   const QAPI: IQSI = this.winRef.nativeWindow.QSI.API
    //   QAPI.unload()
    //   QAPI.load().then(QAPI.run())
    // })
  }

  layoutRendered(event) {
    setTimeout(() => {
      this.isLayoutRendered = true;
      this.userType$.pipe(take(1)).subscribe((user) => {
        if (user !== this.userTypeEnum.CUSTOMER) {
          this.isEntitySelectorRendered = true;
        }
      });
    }, 1000);
  }

  private setFeedback() {
    if (this.country === COUNTRY_CODE.EGYPT) {
      this.isFeedbackEnabled = false;
    } else {
      this.isFeedbackEnabled = true;
    }
  }

  private setLogo() {
    this.country === COUNTRY_CODE.ISRAEL && (this.logo = 'readymix.co.il.white.svg');
    this.neorisLogo && (this.logo = 'neoris-logo.svg');
  }

  private getCookie(name: string): string {
    const match: RegExpMatchArray = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`)) || ([] as any);
    return match[2] ? match[2] : '';
  }

  private setBlueGreen() {
    this.isGreen = this.getCookie(this.BLUE_GREEN_COOKIE_NAME) === this.GREEN_COOKIE_VALUE;
  }

  private onSettingsClick() {
    safeNavigate('/settings');
  }

  private onLegalClick() {
    safeWindowOpen('/public/legal', '_blank');
  }

  private onPrivacyClick() {
    safeWindowOpen('/public/privacy', '_blank');
  }

  private onCemexClick() {
    safeWindowOpen('https://www.cemex.com/', '_blank');
  }

  private async setLegalEntity() {
    this.legalEntityList = this.legalEntityService.legalEntityList;
    this.legalEntityLooader = this.legalEntityService.legalEntityLoader;
    this.legalEntityMessage = this.legalEntityService.legalEntityMessage;
    this.legalEntityActive = this.legalEntityService.legalEntityActive;
    this.subs.push(
      this.legalEntityActive.subscribe((entity) => entity && this.store.dispatch(new SetLegalEntity(entity))),
    );
  }

  private async fetchItems() {
    // Make sure that legal entity is set before turning of internal loader
    // and setting initalized value to true
    this.subs.push(
      this.store.select(getLegalEntity).subscribe((entity) => {
        if (!!entity) {
          if (!this.initialized) {
            this.internalLoader = false;
            this.cdRef.detectChanges();
            this.store.dispatch(new Initialize());
          }
        }
      }),
    );
    await Promise.all([this.fetchMenu(), this.fetchLegalEntities()]);
  }

  private async fetchMenu() {
    try {
      this.menuItems = await this.menuService.getMenu();
    } catch (error) {
      console.log('failed to load menu');
    }
  }

  private async fetchLegalEntities() {
    try {
      this.legalEntityService.initialize(this.authState);
    } catch (error) {
      console.log('failed to load legal entities');
    }
  }

  private setUser() {
    this.userEmail = this.user.userAccount;
    this.userName = this.user.fullName;
    const firstNameInitial = (this.user.firstName[0] || '').toUpperCase();
    const lastNameInitial = (this.user.lastName[0] || '').toUpperCase();
    if (RTL_COUNTRIES.includes(this.country)) {
      this.userInitials = `${lastNameInitial}${firstNameInitial}`;
    } else {
      this.userInitials = `${firstNameInitial}${lastNameInitial}`;
    }
  }

  private setHeaderMenu() {
    this.headerMenuItems = this.store.select(getLanguage).pipe(
      map(() => {
        this.supportContact = {
          label: this.translate.instant('views.header.lbl_contact'),
          id: HEADER_MENU_ITEMS.CONTACT,
        };
        const items = [
          {
            label: this.translate.instant('views.header.lbl_settings'),
            id: HEADER_MENU_ITEMS.SETTINGS,
          },
        ];
        if (this.version === 2) {
          items.push({
            label: this.translate.instant('views.header.lbl_contact'),
            id: HEADER_MENU_ITEMS.CONTACT,
          });
        }
        return items;
      }),
    );
  }

  private setFooterLinks() {
    this.footerItems = this.store.select(getLanguage).pipe(
      map(() => [
        {
          label: this.translate.instant('app.footer.legal'),
          id: FOOTER_LINKS.LEGAL,
        },
        {
          label: this.translate.instant('app.footer.privacy'),
          id: FOOTER_LINKS.PRIVACY,
        },
        {
          label: this.translate.instant('app.footer.web.label'),
          id: FOOTER_LINKS.CMX_WEBSITE,
        },
      ]),
    );
  }

  private setMobileLinks() {
    this.mobileMenuItems = this.store.select(getLanguage).pipe(
      map(() => [
        {
          label: this.translate.instant('views.header.lbl_settings'),
          id: MOBILE_LINKS.SETTINGS,
          icon: 'cmx-icon-settings',
        },
        {
          label: this.translate.instant('app.footer.legal'),
          id: MOBILE_LINKS.LEGAL,
          icon: 'cmx-icon-document',
        },
        {
          label: this.translate.instant('app.footer.privacy'),
          id: MOBILE_LINKS.PRIVACY,
          icon: 'cmx-icon-support-center',
        },
      ]),
    );
  }

  private getFeedbackLinks(language = '') {
    switch (language.substring(3, 5)) {
      case 'CO':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-colombia.ideas.aha.io');
        break;
      case 'CR':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-costarica.ideas.aha.io');
        break;
      case 'CZ':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          'https://cxgofeedback-czechrepublic.ideas.aha.io',
        );
        break;
      case 'EG':
        this.feedbackUrl = null;
        /* keeping formLangUrl as undefined so is not displayed on DOM.
          this.feedbackUrl =
            this.sanitizer.bypassSecurityTrustResourceUrl(
              'https://cxgofeedback-egypt.ideas.aha.io'); */
        break;
      case 'SV':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          'https://cxgofeedback-elsalvador.ideas.aha.io',
        );
        break;
      case 'DE':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-germany.ideas.aha.io');
        break;
      case 'GT':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-guatemala.ideas.aha.io');
        break;
      case 'IL':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-israel.ideas.aha.io');
        break;
      case 'LV':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-latvia.ideas.aha.io');
        break;
      case 'MX':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-mexico.ideas.aha.io');
        break;
      case 'NI':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-nicaragua.ideas.aha.io');
        break;
      case 'PA':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-panama.ideas.aha.io');
        break;
      case 'PE':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-peru.ideas.aha.io');
        break;
      case 'PH':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          'https://cxgofeedback-philippines.ideas.aha.io',
        );
        break;
      case 'PL':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-poland.ideas.aha.io');
        break;
      case 'PR':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          'https://cxgofeedback-puertorico.ideas.aha.io',
        );
        break;
      case 'DO':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          'https://cxgofeedback-republicadominicana.ideas.aha.io',
        );
        break;
      case 'GB':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-uk.ideas.aha.io');
        break;
      case 'US':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-usa.ideas.aha.io');
        break;
      case 'FR':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-france.ideas.aha.io');
        break;
      case 'ES':
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-spain.ideas.aha.io');
        break;
      default:
        this.feedbackUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://cxgofeedback-usa.ideas.aha.io');
        break;
    }
  }

  private setLanguages() {
    this.languages = this.store.select(getLanguages).pipe(
      map((response) => {
        this.languagesRaw = response;
        this.detectDirection(this.languageActiveISO);
        return response.map((lang) => {
          return {
            languageId: lang.languageISO,
            languageName: lang.languageName,
          } as ILanguage;
        });
      }),
    );
    this.languageActive = this.store.select(getLanguage);
  }

  private detectDirection(langISO: string) {
    let direction: Direction;
    const language = this.languagesRaw.find((lang) => {
      return lang.languageISO === langISO;
    });
    if (!language) {
      direction = RTL_LANGUAGES.includes(langISO) ? 'rtl' : 'ltr';
    } else {
      language.textFloat === 'right' ? (direction = 'rtl') : (direction = 'ltr');
    }
    this.store.dispatch(new SetDirection(direction));
  }

  private async setContactUsInfo() {
    const config = await this.countryConfig.getCountryConfig(this.country).toPromise();
    this.contactEmails = !!config && !!config.email ? config.email.split(',') : [];
    this.contactPhones = !!config && !!config.phoneNumber ? config.phoneNumber.split(',') : [];
  }

  private setAppMenu() {
    this.appMenuItems = [
      {
        id: 'developerPortal',
        icon: 'cmx-icon-brakets',
        link: 'https://developers.cemexgo.com',
        name: 'Developer Portal',
      },
    ];
  }

  entityModalEvent() {
    this.entityModalOpen = !this.entityModalOpen;
  }

  loadParameters() {
    this.parametersService.fetchParameters(this.country.trim());
  }

  loadCopyright() {
    if (this.isGetCopyright) {
      this.copyrightService.fetchCopyright();
      this.labelCopyright = this.store.select(getCopyright).pipe(
        map((response) => {
          return response.length > 0
            ? this.removeTags(response[0].HtmlDocument)
            : this.translate.instant('app.footer.copyright');
        }),
      );
    }
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  private removeTags(str) {
    if (str === null || str === '') {
      return false;
    } else {
      str = str.toString();
    }
    return str.replace(/(<([^>]+)>)/gi, '');
  }
}
